import { useEffect, useRef, useState } from "react";
import { Alert, Button, Input, Radio, Select } from "antd";
import { ControllerDiagnosticoSearch } from "../../../Service/Service.consulta";
import {
    REASON_CODE_OPTIONS,
    JUSTIFICACION_MIN,
    SECCIONES_TEXTO,
    SIGNOS_EDITABLES,
    SIGNO_IMC,
    calcularImc,
    RECETA_CAMPOS,
    esRecetaVigente,
    registroSeleccionado,
    valorOriginal,
    diffDiagnosticos,
    razonesRequeridas,
    iniciarEdicion,
} from "../../../utils/auditoriaCorreccion";

const { TextArea } = Input;

// AUDITORIA-CLINICA-REDISENO (FASE 2 · Estado 2 "Corrigiendo") -- cuerpo de la
// tarjeta cuando se está corrigiendo. SUSTITUYE POR COMPLETO al modal de
// corrección: no hay overlay, no se oculta la ficha del paciente y sólo se
// edita una sección a la vez.
//
// El valor ORIGINAL queda a la vista junto al corregido -- el modal actual
// precargaba el textarea con el mismo texto y no había comparación posible.
//
// Motivo + justificación son POR CAMBIO (no una justificación general): una
// sección de texto pide uno; signos vitales pide uno por campo editado y
// diagnósticos uno por cada alta/baja. Es la misma regla que ya exige el
// backend (WORKFLOW-44 §10/§11/§17), aquí sólo se muestra mejor.

const LadoOriginal = ({ valor }) => (
    <div className="ac-lado">
        <span className="ac-etiqueta">Valor original</span>
        <div className="ac-lado__original" data-testid="ac-valor-original">
            {valor === "" || valor === null || valor === undefined ? "— sin valor —" : valor}
        </div>
    </div>
);

function RazonDeCambio({ clave, etiqueta, valor, onChange }) {
    const justificacion = valor?.justification || "";
    // JUSTIFICACION-OPCIONAL: vacía es válida. Sólo se avisa si se ha escrito
    // algo y se queda corta -- opcional no significa "vale cualquier cosa".
    const escrita = justificacion.trim().length > 0;
    const corta = escrita && justificacion.trim().length < JUSTIFICACION_MIN;
    return (
        <div className="ac-razon-bloque" data-testid={`ac-razon-${clave}`}>
            <span className="ac-edicion__razon-titulo">{etiqueta}</span>
            <div className="ac-edicion__razon">
                <div className="ac-lado">
                    <span className="ac-etiqueta">Motivo del cambio *</span>
                    <Select
                        className="w-100"
                        placeholder="Selecciona un motivo"
                        options={REASON_CODE_OPTIONS}
                        value={valor?.reason_code}
                        onChange={(v) => onChange(clave, { reason_code: v })}
                        data-testid={`ac-motivo-${clave}`}
                    />
                </div>
                <div className="ac-lado">
                    <span className="ac-etiqueta">Justificación</span>
                    <TextArea
                        rows={2}
                        value={justificacion}
                        placeholder={`Opcional. Si la escribes, mínimo ${JUSTIFICACION_MIN} caracteres`}
                        onChange={(e) => onChange(clave, { justification: e.target.value })}
                        data-testid={`ac-justificacion-${clave}`}
                    />
                    {escrita && (
                        <span className={`ac-contador${corta ? " is-corto" : ""}`}>
                            {justificacion.trim().length} / mín. {JUSTIFICACION_MIN}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

function CuerpoTexto({ edicion, secciones, onCambio }) {
    const cfg = SECCIONES_TEXTO[edicion.seccion];
    const registros = cfg.coleccion ? secciones[cfg.coleccion] || [] : [];
    return (
        <>
            {registros.length > 1 && (
                <div className="ac-lado">
                    <span className="ac-etiqueta">Registro a corregir</span>
                    <Select
                        className="w-100"
                        value={edicion.registroId}
                        onChange={(v) => {
                            const r = registros.find((x) => x[cfg.idCampo] === v);
                            onCambio({ registroId: v, valores: { [cfg.campo]: r?.[cfg.campo] ?? "" } });
                        }}
                        options={registros.map((r) => ({
                            value: r[cfg.idCampo],
                            label: `#${r[cfg.idCampo]} — ${String(r[cfg.campo] ?? "").slice(0, 60) || "sin contenido"}`,
                        }))}
                        data-testid="ac-selector-registro"
                    />
                </div>
            )}
            <div className="ac-comparacion">
                <LadoOriginal valor={valorOriginal(edicion, secciones)} />
                <div className="ac-lado">
                    <span className="ac-etiqueta">Valor corregido</span>
                    <TextArea
                        rows={4}
                        autoFocus
                        value={edicion.valores[cfg.campo] ?? ""}
                        onChange={(e) => onCambio({ valores: { [cfg.campo]: e.target.value } })}
                        data-testid="ac-valor-corregido"
                    />
                </div>
            </div>
        </>
    );
}

function CuerpoSignos({ edicion, secciones, onCambio }) {
    const registros = secciones.signos_vitales || [];
    const registro = registroSeleccionado(edicion, secciones);
    const imcRecalculado = calcularImc(edicion.valores.peso, edicion.valores.talla);
    return (
        <>
            {registros.length > 1 && (
                <div className="ac-lado">
                    <span className="ac-etiqueta">Registro a corregir</span>
                    <Select
                        className="w-100"
                        value={edicion.registroId}
                        onChange={(v) => {
                            const r = registros.find((x) => x.idsignosvitales === v);
                            const valores = {};
                            for (const c of SIGNOS_EDITABLES) valores[c.id] = r?.[c.id] ?? "";
                            onCambio({ registroId: v, valores, razones: {} });
                        }}
                        options={registros.map((r) => ({ value: r.idsignosvitales, label: `Registro #${r.idsignosvitales}` }))}
                        data-testid="ac-selector-registro"
                    />
                </div>
            )}
            {SIGNOS_EDITABLES.map((campo) => (
                <div className="ac-comparacion" key={campo.id}>
                    <div className="ac-lado">
                        <span className="ac-etiqueta">{campo.label} · original</span>
                        <div className="ac-lado__original" data-testid={`ac-original-${campo.id}`}>
                            {registro?.[campo.id] === null || registro?.[campo.id] === undefined || registro?.[campo.id] === ""
                                ? "— sin valor —"
                                : String(registro[campo.id])}
                        </div>
                    </div>
                    <div className="ac-lado">
                        <span className="ac-etiqueta">{campo.label} · corregido</span>
                        <Input
                            value={edicion.valores[campo.id] ?? ""}
                            onChange={(e) => onCambio({ valores: { [campo.id]: e.target.value } })}
                            data-testid={`ac-corregido-${campo.id}`}
                        />
                    </div>
                </div>
            ))}
            {/* El IMC no se digita: se deriva del peso y la talla (misma
                fórmula del formulario clínico) y se guarda junto al cambio que
                lo provoca. Se muestra para que el auditor vea la consecuencia
                antes de guardar, nunca como campo editable. */}
            <div className="ac-comparacion">
                <div className="ac-lado">
                    <span className="ac-etiqueta">{SIGNO_IMC.label} · original</span>
                    <div className="ac-lado__original">
                        {registro?.imc === null || registro?.imc === undefined || registro?.imc === "" ? "— sin valor —" : String(registro.imc)}
                    </div>
                </div>
                <div className="ac-lado">
                    <span className="ac-etiqueta">{SIGNO_IMC.label} · recalculado</span>
                    <div className="ac-lado__original" data-testid="ac-imc-recalculado">
                        {imcRecalculado === null ? "— requiere peso y talla —" : String(imcRecalculado)}
                    </div>
                    <span className="ac-contador">Derivado de peso y talla; no se edita a mano.</span>
                </div>
            </div>
        </>
    );
}

function CuerpoDiagnosticos({ edicion, secciones, onCambio }) {
    const [opciones, setOpciones] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [errorBusqueda, setErrorBusqueda] = useState(null);
    const timer = useRef(null);
    const { actuales, agregados, retirados } = diffDiagnosticos(edicion, secciones);
    const seleccion = edicion.diagnosticos || [];

    useEffect(() => () => clearTimeout(timer.current), []);

    // Debounce local (no se agrega ninguna dependencia): el catálogo CIE se
    // busca contra el MISMO servicio del flujo clínico normal, nunca texto
    // libre.
    const buscar = (texto) => {
        clearTimeout(timer.current);
        if (!texto || texto.trim().length < 3) {
            setOpciones([]);
            setErrorBusqueda(null);
            return;
        }
        timer.current = setTimeout(async () => {
            setBuscando(true);
            setErrorBusqueda(null);
            const res = await ControllerDiagnosticoSearch(texto);
            setBuscando(false);
            if (res?.success) {
                setOpciones((res.data || []).map((d) => ({ value: d.id, label: `${d.codigo || ""} - ${d.nombre || ""}`, codigo: d.codigo, nombre: d.nombre })));
            } else {
                setOpciones([]);
                setErrorBusqueda(res?.message || "No se pudo buscar el diagnóstico.");
            }
        }, 400);
    };

    const quitar = (iddiagnostico) => onCambio({ diagnosticos: seleccion.filter((d) => d.iddiagnostico !== iddiagnostico) });
    const restaurar = (diag) => onCambio({ diagnosticos: [...seleccion, diag] });

    return (
        <>
            <div>
                <span className="ac-etiqueta">Diagnósticos de la atención</span>
                {actuales.map((d) => {
                    const retirado = retirados.some((x) => x.iddiagnostico === d.iddiagnostico);
                    return (
                        <div className={`ac-diag-fila${retirado ? " is-retirado" : ""}`} key={d.iddiagnostico}>
                            <span className="ac-mono">{d.codigo || "—"}</span>
                            <span className="ac-diag-fila__texto">{d.nombre || `CIE #${d.iddiagnostico}`}</span>
                            <span className="ac-diag-fila__acciones">
                                {retirado ? (
                                    <Button size="small" onClick={() => restaurar(d)} data-testid={`ac-diag-restaurar-${d.iddiagnostico}`}>
                                        Deshacer
                                    </Button>
                                ) : (
                                    <Button size="small" danger onClick={() => quitar(d.iddiagnostico)} data-testid={`ac-diag-retirar-${d.iddiagnostico}`}>
                                        Retirar
                                    </Button>
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>

            {agregados.length > 0 && (
                <div>
                    <span className="ac-etiqueta">Se agregarán</span>
                    {agregados.map((d) => (
                        <div className="ac-diag-fila" key={d.iddiagnostico}>
                            <span className="ac-mono">{d.codigo || "—"}</span>
                            <span className="ac-diag-fila__texto">{d.nombre || `CIE #${d.iddiagnostico}`}</span>
                            <span className="ac-diag-fila__acciones">
                                <Button size="small" onClick={() => quitar(d.iddiagnostico)} data-testid={`ac-diag-cancelar-${d.iddiagnostico}`}>
                                    Cancelar
                                </Button>
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <div className="ac-lado">
                <span className="ac-etiqueta">Agregar diagnóstico (CIE)</span>
                <Select
                    showSearch
                    allowClear
                    className="w-100"
                    placeholder="Escribe al menos 3 caracteres..."
                    filterOption={false}
                    loading={buscando}
                    value={null}
                    options={opciones}
                    onSearch={buscar}
                    notFoundContent={buscando ? "Buscando..." : errorBusqueda || "Sin resultados"}
                    onChange={(_v, option) => {
                        if (!option) return;
                        if (seleccion.some((x) => x.iddiagnostico === option.value)) return;
                        onCambio({ diagnosticos: [...seleccion, { iddiagnostico: option.value, codigo: option.codigo, nombre: option.nombre }] });
                    }}
                    data-testid="ac-diag-buscador"
                />
                {errorBusqueda && <span className="ac-contador is-corto">{errorBusqueda}</span>}
            </div>
        </>
    );
}

function CuerpoRecetas({ edicion, secciones, onCambio }) {
    const vigentes = (secciones.recetas || []).filter(esRecetaVigente);
    const registro = registroSeleccionado(edicion, secciones);

    if (vigentes.length === 0) {
        return (
            <Alert
                type="info"
                showIcon
                message="Todas las recetas de esta atención ya fueron reemplazadas o anuladas; una corrección debe partir de la versión vigente."
            />
        );
    }

    return (
        <>
            <div className="ac-edicion__razon">
                <div className="ac-lado">
                    <span className="ac-etiqueta">Receta vigente a corregir</span>
                    <Select
                        className="w-100"
                        value={edicion.registroId}
                        onChange={(v) => {
                            const r = vigentes.find((x) => x.idreceta === v);
                            const valores = {};
                            for (const c of RECETA_CAMPOS) valores[c.id] = r?.[c.id] ?? "";
                            onCambio({ registroId: v, valores });
                        }}
                        options={vigentes.map((r) => ({ value: r.idreceta, label: `#${r.idreceta} — ${r.medicamentos || "sin medicamento"}` }))}
                        data-testid="ac-selector-registro"
                    />
                </div>
                <div className="ac-lado">
                    <span className="ac-etiqueta">Qué hacer con ella</span>
                    <Radio.Group
                        value={edicion.accion}
                        onChange={(e) => onCambio({ accion: e.target.value })}
                        data-testid="ac-receta-accion"
                    >
                        <Radio value="REPLACE">Sustituir por una versión corregida</Radio>
                        <Radio value="VOID">Anular sin reemplazo</Radio>
                    </Radio.Group>
                </div>
            </div>

            {edicion.accion === "REPLACE" &&
                RECETA_CAMPOS.map((campo) => (
                    <div className="ac-comparacion" key={campo.id}>
                        <div className="ac-lado">
                            <span className="ac-etiqueta">{campo.label} · original</span>
                            <div className="ac-lado__original">{registro?.[campo.id] || "— sin valor —"}</div>
                        </div>
                        <div className="ac-lado">
                            <span className="ac-etiqueta">
                                {campo.label} · corregido {campo.obligatorio ? "*" : ""}
                            </span>
                            <TextArea
                                rows={campo.id === "medicamentos" || campo.id === "indicaciones" ? 2 : 1}
                                value={edicion.valores[campo.id] ?? ""}
                                onChange={(e) => onCambio({ valores: { [campo.id]: e.target.value } })}
                                data-testid={`ac-receta-${campo.id}`}
                            />
                        </div>
                    </div>
                ))}

            {edicion.accion === "VOID" && (
                <Alert
                    type="warning"
                    showIcon
                    message="La receta quedará anulada. La original conserva su contenido clínico intacto; sólo cambia su estado."
                />
            )}
        </>
    );
}

function AtencionSeccionEdicion({ edicion, secciones, onCambio, onRazon, error }) {
    const razones = razonesRequeridas(edicion, secciones);
    const Cuerpo = SECCIONES_TEXTO[edicion.seccion]
        ? CuerpoTexto
        : edicion.seccion === "signos_vitales"
        ? CuerpoSignos
        : edicion.seccion === "diagnosticos"
        ? CuerpoDiagnosticos
        : CuerpoRecetas;

    return (
        <div className="ac-edicion" data-testid={`ac-edicion-${edicion.seccion}`}>
            {/* El error de guardado se muestra en la PROPIA tarjeta y no borra
                nada de lo escrito (criterio de aceptación 8). */}
            {error && <Alert type="error" showIcon message={error} data-testid="ac-edicion-error" />}
            <Cuerpo edicion={edicion} secciones={secciones} onCambio={onCambio} />
            {razones.map((r) => (
                <RazonDeCambio key={r.clave} clave={r.clave} etiqueta={r.etiqueta} valor={edicion.razones?.[r.clave]} onChange={onRazon} />
            ))}
        </div>
    );
}

export { iniciarEdicion };
export default AtencionSeccionEdicion;
